/*eslint-disable max-lines*/
import {
  useResolveLocationAlarmIncident,
  useSendActionToZapier,
  useUpdateLocationAlarm,
} from '@hakimo-ui/hakimo/data-access';
import {
  AIRecommendation,
  Location,
  LocationAlarmIncidentDTO,
  StatusType,
  ZapierActionPayload,
} from '@hakimo-ui/hakimo/types';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import {
  eventTracker,
  RESOLUTION_REASONS,
  useCanUpdateLocationAlarmStatus,
  useUser,
} from '@hakimo-ui/hakimo/util';
import {
  Alert,
  Button,
  Modal,
  Selectable,
  Textarea,
} from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import { useState } from 'react';

interface Props {
  locationAlarmId: string;
  locationIncident: LocationAlarmIncidentDTO;
  onStatusChange?: (status: StatusType) => void;
  onClose: () => void;
  cameraNames?: string[];
  aiOperatorRep?: AIRecommendation;
  location: Location;
}

export function ResolveIncidentModal(props: Props) {
  const {
    locationAlarmId,
    locationIncident,
    onClose,
    onStatusChange,
    cameraNames = [],
    aiOperatorRep,
    location,
  } = props;
  const [textVal, setTextVal] = useState('');
  const [resolutionReason, setResolutionReason] = useState<
    Selectable | undefined
  >();

  const user = useUser();
  const canUpdateStatus = useCanUpdateLocationAlarmStatus();
  const { mutate: sendActionToZapierMutation, isLoading: isSendingToZapier } =
    useSendActionToZapier();

  const onSuccess = () => {
    onStatusChange && onStatusChange(StatusType.RESOLVED);
    onClose();
  };

  const resolveAlarmMutation = useUpdateLocationAlarm(locationAlarmId, {
    onSuccess,
  });
  const resolveIncidentMutation = useResolveLocationAlarmIncident();

  const onTextChange = (val: string) => setTextVal(val);

  const onSubmit = () => {
    const locationIncidentPayload = {
      incident_id: locationIncident.id,
      resolved_by: user.id,
      resolution_comment: textVal,
      location_alarm_id: locationAlarmId,
      resolution_reason: resolutionReason?.id,
    };
    resolveIncidentMutation.mutate(locationIncidentPayload);

    const resolveAlarmPayload = {
      status: StatusType.RESOLVED,
      comment: `Escalation - ${textVal}`,
    };
    resolveAlarmMutation.mutate(resolveAlarmPayload);
  };

  const handleSubmit = () => {
    const startTime = dayjs(locationIncident.incidentStartTimeUtc).tz(
      location.timezone
    );
    const partitionKey = dayjs
      .utc(locationIncident.incidentStartTimeUtc)
      .format('YYYYMM');
    const endTime = dayjs().tz(location.timezone);
    const zapierPayload: ZapierActionPayload = {
      alarm_id: locationAlarmId,
      site_name: location.name,
      alarm_date: startTime.format('YYYY-MM-DD'),
      alarm_time: startTime.format('HH:mm:ss'),
      alarm_resolve_date: endTime.format('YYYY-MM-DD'),
      alarm_resolve_time: endTime.format('HH:mm:ss'),
      location_description: location.description,
      operator_email: user.email,
      relevant_cameras: cameraNames.join(', '),
      timezone: location.timezone,
      timestamp: new Date()
        .toLocaleString('en-US', { timeZone: 'Asia/Kolkata', hour12: false })
        .replace(',', ''),
      // videoPath: `https://portal.hakimo.ai/location-alarms/${locationAlarmId}`,
      videoPath: `${window.location.origin}/location-alarms/${locationAlarmId}?partitionKey=${partitionKey}`,
      alarmType: '',
      dispatchType: '',
      dispatchAgencyName: '',
      dispatcherName_ID: '',
      dispatch_cancelled: '',
      disposition: resolutionReason?.id,
      incident_type: aiOperatorRep?.headline || '',
      operator_id: user.name,
      PD_call_reference: '',
      resolution_comment: textVal,
      talkdown_used: '',
      soc: user.tenant.name,
      location_incident_id: String(locationIncident.id),
    };
    sendActionToZapierMutation(zapierPayload);
    onSubmit();
  };

  const actions = (
    <>
      {canUpdateStatus && (
        <Button
          title="Resolve"
          variant="primary"
          disabled={
            textVal === '' ||
            !resolutionReason ||
            isSendingToZapier ||
            resolveAlarmMutation.isLoading ||
            resolveIncidentMutation.isLoading
          }
          onClick={handleSubmit}
          loading={
            isSendingToZapier ||
            resolveAlarmMutation.isLoading ||
            resolveIncidentMutation.isLoading
          }
          onSideEffect={eventTracker(
            'close_and_resolve_active_location_incident'
          )}
        >
          Resolve
        </Button>
      )}
      <Button onClick={onClose}>Cancel</Button>
    </>
  );

  return (
    <Modal
      title="Resolve Incident"
      onClose={onClose}
      open
      closable
      footer={actions}
    >
      <div className="space-y-4 p-8">
        {resolveAlarmMutation.isError ||
          (resolveIncidentMutation.isError && (
            <Alert type="error">
              Error submitting and resolving the incident
            </Alert>
          ))}
        <Alert type="info">
          Alarm should only be resolved once site POC and PD are alerted about
          the incident.
        </Alert>
        <div>Select resolution reason</div>
        <SelectMenu
          value={resolutionReason}
          placeholder="Select a reason"
          items={RESOLUTION_REASONS}
          displayValue={(item) => item?.name ?? ''}
          onChange={setResolutionReason}
          id={(item) => item?.id || ''}
        />
        <div>Provide a brief comment on the escalation</div>
        <Textarea
          value={textVal}
          rows={4}
          placeholder="Describe the incident and the actions taken"
          onChange={onTextChange}
        />
      </div>
    </Modal>
  );
}

export default ResolveIncidentModal;
